import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Flag, LogOut, Send, ShieldCheck, Shuffle, ThumbsUp, Trash2, Volume2 } from "lucide-react";
import { RefreshButton } from "./RefreshButton";
import { getDeviceId } from "../useDeviceId";
import { DEFAULT_SPEAKER, SPEECH_ACCENTS, SPEECH_MAX_CHARACTERS, SPEECH_SPEAKERS, SPEECH_VOICES } from "../../shared/speech";
import { SPEECH_VOICE_CHOICES, type SpeechVoice } from "../../shared/speech-catalogue";
import type { AlertRelevance } from "../../shared/types";
import { BillingPanel } from "./BillingPanel";
import { FeedbackPanel } from "./FeedbackPanel";
import { AdminSection } from "./AdminSection";

interface Overview {
	subscriptions: Array<{ device: string; owner: string | null; updated_at: number; travel_started_at: number | null; config: string }>;
	states: Array<{
		device: string;
		owner: string | null;
		atMs: number;
		lastEvent: string;
		build: string | null;
		shell: string | null;
		phase: string | null;
		direction: string | null;
		visibility: string | null;
		online: boolean | null;
		lat: number | null;
		lon: number | null;
		accuracyMeters: number | null;
		fixAtMs: number | null;
		metersFromHome: number | null;
		metersToStation: number | null;
		metersFromSchool: number | null;
	}>;
	devices: Array<{ deviceId: string; device: string; username: string; owner: string | null; approved_at: number; last_seen_at: number }>;
	diagnostics: Array<{ id: number; device: string; owner: string | null; at: number; event: string; detail: string }>;
	bartAlerts: Array<{ fingerprint: string; header: string; description: string; effect: string; activeFromMs: number | null; activeUntilMs: number | null; informedRouteIds: string[]; informedStopIds: string[]; hidden: boolean }>;
	sayings: Array<{ id: string; audience: string; text: string; source: string; votes: number; flagged: boolean; flaggedBy: string | null; retired: boolean; today: boolean }>;
	sayingSeed: number;
	logins: Array<{ username: string; device: string; approved_at: number }>;
	loginOptions: string[];
}

type SortDirection = "asc" | "desc";
interface SortState<Key extends string> { key: Key; direction: SortDirection }

function sortedRows<Row, Key extends string>(rows: Row[], sort: SortState<Key>, value: (row: Row, key: Key) => string | number | null): Row[] {
	return [...rows].sort((left, right) => {
		const leftValue = value(left, sort.key);
		const rightValue = value(right, sort.key);
		if (leftValue == null) return rightValue == null ? 0 : 1;
		if (rightValue == null) return -1;
		const comparison = typeof leftValue === "number" && typeof rightValue === "number" ? leftValue - rightValue : String(leftValue).localeCompare(String(rightValue));
		return sort.direction === "asc" ? comparison : -comparison;
	});
}

function SortHeader<Key extends string>({ label, column, sort, onChange }: { label: string; column: Key; sort: SortState<Key>; onChange: (sort: SortState<Key>) => void }) {
	const active = sort.key === column;
	const direction = active ? sort.direction : "asc";
	return <th aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}>
		<button className="admin-sort" onClick={() => onChange({ key: column, direction: active && direction === "asc" ? "desc" : "asc" })}>
			{label}{active ? direction === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} /> : null}
		</button>
	</th>;
}

async function responseMessage(response: Response): Promise<string> {
	const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
	return body?.error?.message ?? `Request failed (${response.status}).`;
}

function when(value: number | null): string {
	return value == null ? "—" : new Date(value).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function readableJson(value: string): string {
	try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
}

/** Kilometres past a point where the exact metre stops telling you anything. */
function metres(value: number | null): string {
	if (value == null) return "—";
	return value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
}

function deviceJourneyLabel(phase: string | null, direction: string | null): string {
	const shownPhase = phase === "at_home" && direction === "home" ? "at_school" : phase;
	const shownDirection = direction === "home" ? "inbound" : direction === "school" ? "outbound" : direction;
	return [shownPhase, shownDirection].filter(Boolean).join(" · ") || "\u2014";
}
/** How long ago, because a timestamp does not say whether a phone has gone quiet. */
function since(value: number | null, nowMs: number): string {
	if (value == null) return "\u2014";
	const seconds = Math.max(0, Math.round((nowMs - value) / 1000));
	if (seconds < 60) return `${seconds}s ago`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, "0")}s ago`;
	const hours = Math.floor(minutes / 60);
	return hours < 24 ? `${hours}h ${minutes % 60}m ago` : `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
}
/**
 * Auditioning voices, not a feature.
 *
 * The button is a real tap, which is the only way iOS plays anything on demand — so this is
 * also the honest test of whether a generated clip is worth wiring into the reminders.
 */
function SpeechPlayground() {
	const [text, setText] = useState("Time to leave for Civic Center.");
	const [speaker, setSpeaker] = useState<string>(DEFAULT_SPEAKER);
	const [status, setStatus] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [audioUrl, setAudioUrl] = useState<string | null>(null);

	const generate = async () => {
		setBusy(true);
		setStatus(null);
		const startedAt = Date.now();
		try {
			const response = await fetch("/api/admin/speech", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ text, speaker }),
			});
			if (!response.ok) throw new Error(await responseMessage(response));
			const blob = await response.blob();
			setAudioUrl((current) => {
				if (current) URL.revokeObjectURL(current);
				return URL.createObjectURL(blob);
			});
			setStatus(`${text.trim().length} characters · ${Math.round(blob.size / 1024)} KB · ${Date.now() - startedAt} ms`);
		} catch (cause) {
			setStatus(cause instanceof Error ? cause.message : "Could not generate speech.");
		} finally {
			setBusy(false);
		}
	};

	return <AdminSection title="Voice playground">
		<div className="admin-speech">
			<label>Text<textarea rows={3} maxLength={SPEECH_MAX_CHARACTERS} value={text} onChange={(event) => setText(event.target.value)} /></label>
			<label>Speaker<select value={speaker} onChange={(event) => setSpeaker(event.target.value)}>
				{SPEECH_ACCENTS.map((accent) => {
					const names = SPEECH_SPEAKERS.filter((name) => SPEECH_VOICES[name].accent === accent);
					return <optgroup key={accent} label={`${accent} (${names.length})`}>
						{names.map((name) => <option key={name} value={name}>
							{`${name} · ${SPEECH_VOICES[name].gender} · ${SPEECH_VOICES[name].notes}`}
						</option>)}
					</optgroup>;
				})}
			</select></label>
			<button className="admin-button" disabled={busy || text.trim().length === 0} onClick={() => void generate()}>
				<Volume2 size={17} /> {busy ? "Generating…" : "Generate"}
			</button>
			{status && <p className="admin-status">{status}</p>}
			{audioUrl && <audio controls autoPlay src={audioUrl} />}
		</div>
	</AdminSection>;
}

interface SpeechEntry {
	variant: unknown;
	voice: SpeechVoice;
	text: string;
	key: string;
	cached: boolean;
	bytes: number;
}

/**
 * The alert summariser, on wording you supply.
 *
 * BART rarely posts anything worth summarising, so the only other chance to judge this would be
 * the morning it matters. Paste a real notice in and see what riders would have been shown.
 */
function DigestPlayground() {
	const [journey, setJourney] = useState("Rockridge → San Francisco");
	const [lines, setLines] = useState("Yellow-S");
	const [text, setText] = useState(
		"BART is experiencing a major delay on the Yellow line in the Antioch direction due to a medical emergency at Rockridge. Trains are single tracking. Riders should expect delays of 20 minutes or more. We apologise for the inconvenience.",
	);
	const [relevance, setRelevance] = useState<AlertRelevance>("station");
	const [summary, setSummary] = useState<string | null>(null);
	const [status, setStatus] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const run = async () => {
		setBusy(true);
		setStatus(null);
		const startedAt = Date.now();
		try {
			// One notice per blank line, which is how a real multi-alert morning arrives.
			const alerts = text
				.split(/\n\s*\n/)
				.map((block) => block.trim())
				.filter(Boolean)
				.map((block) => ({ header: block.split("\n")[0] ?? "", description: block, relevance }));
			const response = await fetch("/api/admin/digest", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ alerts, journey: { label: journey, lines: lines.split(",").map((line) => line.trim()).filter(Boolean) } }),
			});
			if (!response.ok) throw new Error(await responseMessage(response));
			const body = (await response.json()) as { summary: string };
			setSummary(body.summary);
			setStatus(`${alerts.length} notice(s) · ${text.length} → ${body.summary.length} characters · ${Date.now() - startedAt} ms`);
		} catch (cause) {
			setStatus(cause instanceof Error ? cause.message : "Could not summarise.");
		} finally {
			setBusy(false);
		}
	};

	return <AdminSection title="Alert digest">
		<div className="admin-speech">
			<label>Journey<input value={journey} onChange={(event) => setJourney(event.target.value)} /></label>
			<label>Lines, comma separated<input value={lines} onChange={(event) => setLines(event.target.value)} /></label>
			<label>How BART tagged these notices<select value={relevance} onChange={(event) => setRelevance(event.target.value as AlertRelevance)}>
				<option value="station">Names a station on this journey</option>
				<option value="line">Names a line this journey uses</option>
				<option value="system">General notice, names nothing</option>
				<option value="elsewhere">Elsewhere on the network</option>
			</select></label>
			<label>Notices, one per blank line<textarea rows={6} value={text} onChange={(event) => setText(event.target.value)} /></label>
			<button className="admin-button" disabled={busy || text.trim().length === 0} onClick={() => void run()}>
				{busy ? "Summarising…" : "Summarise"}
			</button>
			{status && <p className="admin-status">{status}</p>}
			{summary && <p className="admin-digest">{summary}</p>}
		</div>
	</AdminSection>;
}

/**
 * The generated vocabulary, and what is missing from it.
 *
 * Regenerate is the point of this table: Aura never renders a phrase the same way twice, so a
 * take you like has to be captured deliberately and then left alone.
 */
function SpeechCache() {
	const [entries, setEntries] = useState<SpeechEntry[] | null>(null);
	const [orphans, setOrphans] = useState(0);
	const [status, setStatus] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [playing, setPlaying] = useState<string | null>(null);

	const load = async () => {
		const response = await fetch("/api/admin/speech/cache");
		if (!response.ok) throw new Error(await responseMessage(response));
		const body = await response.json() as { expected: SpeechEntry[]; orphans: unknown[] };
		setEntries(body.expected);
		setOrphans(body.orphans.length);
	};

	useEffect(() => { void load().catch((cause) => setStatus(cause instanceof Error ? cause.message : "Could not load the cache.")); }, []);

	const act = async (action: () => Promise<string>) => {
		setBusy(true);
		setStatus(null);
		try {
			setStatus(await action());
			await load();
		} catch (cause) {
			setStatus(cause instanceof Error ? cause.message : "Request failed.");
		} finally {
			setBusy(false);
		}
	};

	const post = async (body: unknown): Promise<Response> => {
		const response = await fetch("/api/admin/speech/cache", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		if (!response.ok) throw new Error(await responseMessage(response));
		return response;
	};

	const missing = entries?.filter((entry) => !entry.cached).length ?? 0;

	return <AdminSection title={<>Voice cache <span className="admin-disclosure__count">{entries ? `${entries.length - missing}/${entries.length}` : "…"}</span></>} defaultOpen={false}>
		<div className="admin-section__head">
			<div className="admin-filters">
				<button className="admin-add" disabled={busy} onClick={() => void act(async () => {
					const result = await (await post({ all: true })).json() as { generated: number; total: number };
					return `Generated ${result.generated} of ${result.total}.`;
				})}>Precompute all</button>
				<button className="admin-revoke" disabled={busy || orphans === 0} onClick={() => void act(async () => {
					const response = await fetch("/api/admin/speech/cache", {
						method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ orphans: true }),
					});
					if (!response.ok) throw new Error(await responseMessage(response));
					return `Removed ${((await response.json()) as { removed: number }).removed} unused.`;
				})}>Remove unused ({orphans})</button>
			</div>
		</div>
		{status && <p className="admin-status" role="status">{status}</p>}
		{playing && <audio controls autoPlay src={playing} />}
		<div className="admin-table-wrap"><table>
			<thead><tr><th>Phrase</th><th>Voice</th><th>Cached</th><th>Size</th><th>Actions</th></tr></thead>
			<tbody>{entries?.map((entry) => <tr key={entry.key}>
				<td>{entry.text}</td>
				<td>{SPEECH_VOICE_CHOICES[entry.voice]?.label ?? entry.voice}</td>
				<td>{entry.cached ? "yes" : "—"}</td>
				<td>{entry.cached ? `${Math.round(entry.bytes / 1024)} KB` : "—"}</td>
				<td>
					<div className="admin-rowActions">
						<button className="admin-revoke" disabled={busy || !entry.cached}
							onClick={() => setPlaying(`/api/speech/audio/${encodeURIComponent(entry.key)}`)}>Play</button>
						<button className="admin-revoke" disabled={busy} onClick={() => void act(async () => {
							await post({ variant: entry.variant, voice: entry.voice, force: true });
							return "Regenerated.";
						})}>Regenerate</button>
					</div>
				</td>
			</tr>)}</tbody>
		</table></div>
	</AdminSection>;
}

/**
 * Judging the sayings.
 *
 * Nothing here writes wording. The pool is seeded from the bundle, and this is where it gets
 * ranked: an upvote is a keeper, a flag takes it out of rotation but keeps it as a note on what
 * misses, and retiring is for good. Reshuffling changes every day's pick at once, which is the
 * only reroll there is — deliberately not something the reader can reach.
 */
function Sayings({
	sayings,
	seed,
	busy,
	act,
	reload,
}: {
	sayings: Overview["sayings"];
	seed: number;
	busy: boolean;
	act: (action: () => Promise<void>) => void;
	reload: () => Promise<void>;
}) {
	const [audience, setAudience] = useState("all");

	const judge = (body: Record<string, unknown>) => act(async () => {
		const response = await fetch("/api/admin/saying", {
			method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
		});
		if (!response.ok) throw new Error(await responseMessage(response));
		await reload();
	});

	const shown = sayings.filter((saying) => audience === "all" || saying.audience === audience);
	const live = sayings.filter((saying) => !saying.flagged && !saying.retired).length;

	return <AdminSection title={<>Sayings <span className="admin-disclosure__count">{live}/{sayings.length} in rotation</span></>} defaultOpen={false}>
		<div className="admin-section__head">
			<div className="admin-filters">
				<label>Audience<select value={audience} onChange={(event) => setAudience(event.target.value)}>
					<option value="all">Both</option>
					<option value="school">School</option>
					<option value="home">Home</option>
				</select></label>
				<button className="admin-add" disabled={busy} title={`Seed ${seed}`} onClick={() => judge({ reshuffle: true })}>
					<Shuffle size={15} /> Reshuffle
				</button>
			</div>
		</div>
		<div className="admin-table-wrap"><table>
			<thead><tr><th>Audience</th><th>Saying</th><th>Votes</th><th>Actions</th></tr></thead>
			<tbody>{shown.map((saying) => <tr key={saying.id} className={saying.retired || saying.flagged ? "admin-saying--out" : undefined}>
				<td>{saying.audience}{saying.today && <span className="admin-saying__today">today</span>}</td>
				<td>{saying.text}</td>
				<td>{saying.votes || "—"}</td>
				<td><div className="admin-rowActions">
					<button className="admin-revoke" disabled={busy} title="Upvote" onClick={() => judge({ id: saying.id, verdict: "up" })}>
						<ThumbsUp size={15} />
					</button>
					<button className="admin-revoke" disabled={busy || saying.retired}
						onClick={() => judge({ id: saying.id, verdict: saying.flagged ? "unflag" : "flag" })}>
						<Flag size={15} />{saying.flagged ? "Unflag" : "Flag"}
					</button>
					<button className="admin-revoke" disabled={busy}
						onClick={() => judge({ id: saying.id, verdict: saying.retired ? "restore" : "retire" })}>
						<Trash2 size={15} />{saying.retired ? "Restore" : "Delete"}
					</button>
				</div></td>
			</tr>)}</tbody>
		</table></div>
	</AdminSection>;
}

export function AdminConsole() {
	const [authenticated, setAuthenticated] = useState<boolean | null>(null);
	const [currentUsername, setCurrentUsername] = useState<string | null>(null);
	const [overview, setOverview] = useState<Overview | null>(null);
	// Ticks so the ages count up on their own; a stale phone should become obvious without a reload.
	const [nowMs, setNowMs] = useState(() => Date.now());
	useEffect(() => {
		const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, []);
	const [username, setUsername] = useState("");
	const [challengeId, setChallengeId] = useState<string | null>(null);
	const [code, setCode] = useState("");
	const [newLoginName, setNewLoginName] = useState("");
	const [status, setStatus] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [approvedDeviceFilter, setApprovedDeviceFilter] = useState("");
	const [subscriptionDeviceFilter, setSubscriptionDeviceFilter] = useState("");
	const [deviceFilter, setDeviceFilter] = useState("all");
	const [eventFilter, setEventFilter] = useState("");
	const [loginSort, setLoginSort] = useState<SortState<"username" | "device" | "approved_at">>({ key: "username", direction: "asc" });
	const [approvedSort, setApprovedSort] = useState<SortState<"owner" | "device" | "approved_at" | "last_seen_at">>({ key: "last_seen_at", direction: "desc" });
	const [subscriptionSort, setSubscriptionSort] = useState<SortState<"device" | "owner" | "updated_at" | "travel_started_at" | "config">>({ key: "updated_at", direction: "desc" });
	const [eventSort, setEventSort] = useState<SortState<"at" | "device" | "owner" | "event" | "detail">>({ key: "at", direction: "desc" });

	const load = async () => {
		const sessionResponse = await fetch("/api/admin/session");
		if (!sessionResponse.ok) throw new Error(await responseMessage(sessionResponse));
		const session = await sessionResponse.json() as { authenticated: boolean; username: string | null };
		if (!session.authenticated) { setAuthenticated(false); setCurrentUsername(null); setOverview(null); return; }
		const response = await fetch("/api/admin/overview");
		if (response.status === 401) { setAuthenticated(false); setCurrentUsername(null); setOverview(null); return; }
		if (!response.ok) throw new Error(await responseMessage(response));
		setOverview(await response.json() as Overview);
		setCurrentUsername(session.username);
		setAuthenticated(true);
	};

	useEffect(() => { void load().catch((cause) => setStatus(cause instanceof Error ? cause.message : "Could not load admin data.")); }, []);

	/*
	 * Kept fresh on its own, because the ages counting up against data that never changes is worse
	 * than a still clock: it reads as a phone going quiet when it is the console that stopped
	 * asking. Paused when the tab is hidden — the overview is an expensive query and nobody is
	 * looking, and a failure here is left silent so a dropped poll cannot bury a real error.
	 */
	useEffect(() => {
		if (authenticated !== true) {
			return;
		}
		const refresh = () => {
			if (document.visibilityState === "visible") {
				void load().catch(() => undefined);
			}
		};
		const timer = window.setInterval(refresh, 20_000);
		document.addEventListener("visibilitychange", refresh);
		return () => {
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", refresh);
		};
	}, [authenticated]);

	const act = async (action: () => Promise<void>) => {
		setBusy(true); setStatus(null);
		try { await action(); } catch (cause) { setStatus(cause instanceof Error ? cause.message : "Request failed."); }
		finally { setBusy(false); }
	};

	if (authenticated !== true) {
		return <main className="admin admin--login">
			<section className="admin-login">
				<div className="admin-brand"><img src="/icons/icon-192.png" alt="" /><h1>GoTime Admin</h1></div>
				<p>Enter your username to receive a short-lived code on your registered device.</p>
				<form onSubmit={(event) => { event.preventDefault(); void act(async () => {
					const response = await fetch("/api/admin/challenge", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username }) });
					if (!response.ok) throw new Error(await responseMessage(response));
					const result = await response.json() as { challengeId: string; deviceLabel: string };
					if (!/^[A-Za-z0-9_-]{16,64}$/.test(result.challengeId) || !/^[a-f0-9]{3}$/.test(result.deviceLabel)) throw new Error("Could not request an admin code.");
					setChallengeId(result.challengeId);
					setStatus(`Code sent to device ${result.deviceLabel}.`);
				}); }}>
					<label htmlFor="admin-username">Username</label>
					<input id="admin-username" autoCapitalize="none" autoComplete="username" spellCheck={false} value={username} onChange={(event) => { setUsername(event.target.value); setChallengeId(null); setCode(""); }} placeholder="Your name" />
					<button className="admin-button" disabled={busy || !username.trim()} type="submit"><Send size={17} /> Send code</button>
				</form>
				<form onSubmit={(event) => { event.preventDefault(); void act(async () => {
					const response = await fetch("/api/admin/verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeId, deviceId: getDeviceId(), code }) });
					if (!response.ok) throw new Error(await responseMessage(response));
					setCode(""); setChallengeId(null); await load();
				}); }}>
					<label htmlFor="admin-code">Six-digit code</label>
					<input id="admin-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder="000000" />
					<button className="admin-button" disabled={busy || !challengeId || code.length !== 6} type="submit"><ShieldCheck size={17} /> Verify</button>
				</form>
				{status && <p className="admin-status" role="status">{status}</p>}
			</section>
		</main>;
	}

	const deviceOptions = [...new Set([...(overview?.subscriptions.map((row) => row.device) ?? []), ...(overview?.diagnostics.map((row) => row.device) ?? [])])].sort();
	const loginRows = sortedRows(overview?.logins ?? [], loginSort, (row, key) => row[key]);
	const approvedRows = sortedRows(
		overview?.devices.filter((row) => `${row.username} ${row.device}`.toLowerCase().includes(approvedDeviceFilter.trim().toLowerCase())) ?? [],
		approvedSort,
		(row, key) => row[key],
	);
	const subscriptionRows = sortedRows(
		overview?.subscriptions.filter((row) => row.device.toLowerCase().includes(subscriptionDeviceFilter.trim().toLowerCase())) ?? [],
		subscriptionSort,
		(row, key) => row[key],
	);
	const eventRows = sortedRows(
		overview?.diagnostics.filter((row) => (deviceFilter === "all" || row.device === deviceFilter) && row.event.toLowerCase().includes(eventFilter.toLowerCase())) ?? [],
		eventSort,
		(row, key) => row[key],
	);
	return <main className="admin">
		<header className="admin-head"><div className="admin-heading"><span>Operations</span><div className="admin-heading__title"><img src="/icons/icon-192.png" alt="" /><h1>GoTime Admin</h1></div></div><nav>
			<RefreshButton label="Refresh" onClick={() => void act(load)} busy={busy} />
			<button title="Log out" aria-label="Log out" onClick={() => void act(async () => { await fetch("/api/admin/session", { method: "DELETE" }); setAuthenticated(false); setCurrentUsername(null); setOverview(null); })}><LogOut size={18} /></button>
		</nav></header>
		{status && <p className="admin-status" role="status">{status}</p>}
		<FeedbackPanel />
		<AdminSection title="Summary"><div className="admin-summary">
			<div><strong>{overview?.subscriptions.length ?? 0}</strong><span>alert subscriptions</span></div>
			<div><strong>{overview?.logins.length ?? 0}</strong><span>allowed logins</span></div>
			<div><strong>{overview?.devices.length ?? 0}</strong><span>approved devices</span></div>
			<div><strong>{overview?.diagnostics.length ?? 0}</strong><span>events in 24 hours</span></div>
		</div></AdminSection>
		<BillingPanel />
		{/*
		  Built from diagnostics rather than a table of its own, so there is no second stream to keep
		  in sync. A phone appears twice on purpose when it runs both the app and the web app.
		*/}
		<AdminSection title="Devices now"><div className="admin-table-wrap"><table><thead><tr>
			<th>Device</th><th>Owner</th><th>Running</th><th>Phase</th><th>Position</th><th>Fix taken</th><th>Home / station / school</th><th>Heard from</th><th>Last event</th>
		</tr></thead><tbody>
			{(overview?.states ?? []).map((row) => (
				<tr key={`${row.device}-${row.atMs}`}>
					<td><code>{row.device}</code></td>
					<td className="admin-name">{row.owner ?? "\u2014"}</td>
					<td>{row.shell ? `iOS app ${row.shell}` : "web app"}{row.build ? ` \u00b7 ${row.build}` : ""}{row.online === false ? " \u00b7 offline" : ""}{row.visibility === "hidden" ? " \u00b7 backgrounded" : ""}</td>
					<td>{deviceJourneyLabel(row.phase, row.direction)}</td>
					<td>{row.lat == null || row.lon == null ? "\u2014" : (
						<a href={`https://www.google.com/maps?q=${row.lat},${row.lon}`} target="_blank" rel="noreferrer">
							{`${row.lat.toFixed(5)}, ${row.lon.toFixed(5)}${row.accuracyMeters == null ? "" : ` \u00b1${Math.round(row.accuracyMeters)} m`}`}
						</a>
					)}</td>
					<td className="admin-time">{row.fixAtMs == null ? "\u2014" : `${since(row.fixAtMs, nowMs)} \u00b7 ${when(row.fixAtMs)}`}</td>
					<td>{[metres(row.metersFromHome), metres(row.metersToStation), metres(row.metersFromSchool)].join(" / ")}</td>
					<td className="admin-time">{`${since(row.atMs, nowMs)} \u00b7 ${when(row.atMs)}`}</td>
					<td>{row.lastEvent}</td>
				</tr>
			))}
			{overview && overview.states.length === 0 ? <tr><td colSpan={9} className="admin-empty">No device has reported in the last week.</td></tr> : null}
		</tbody></table></div></AdminSection>
		<AdminSection title="Current BART alerts"><div className="admin-bart-list">
			{overview?.bartAlerts.length ? overview.bartAlerts.map((alert) => {
				const scope = [...alert.informedRouteIds.map((route) => `Route ${route}`), ...alert.informedStopIds].join(" · ");
				return <article className={`admin-bart-alert${alert.hidden ? " admin-bart-alert--hidden" : ""}`} key={alert.fingerprint}>
					<div><div className="admin-bart-alert__title"><strong>{alert.header || "BART.gov Alert"}</strong>{alert.hidden && <span>Hidden</span>}</div>
						{scope && <div className="admin-bart-alert__scope">{scope}</div>}<p>{alert.description}</p></div>
					<button className="admin-alert-action" onClick={() => void act(async () => {
						const response = await fetch("/api/admin/bart-alert", { method: alert.hidden ? "DELETE" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fingerprint: alert.fingerprint }) });
						if (!response.ok) throw new Error(await responseMessage(response));
						await load();
					})}>{alert.hidden ? <Eye size={15} /> : <EyeOff size={15} />}{alert.hidden ? "Show" : "Hide"}</button>
				</article>;
			}) : <p className="admin-empty">No current BART alerts.</p>}
		</div></AdminSection>
		<Sayings sayings={overview?.sayings ?? []} seed={overview?.sayingSeed ?? 0} busy={busy} act={act} reload={load} />
		<AdminSection title="Allowed logins"><div className="admin-section__head"><form className="admin-inline-form" onSubmit={(event) => { event.preventDefault(); void act(async () => {
			const response = await fetch("/api/admin/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username: newLoginName }) });
			if (!response.ok) throw new Error(await responseMessage(response));
			setNewLoginName(""); await load();
		}); }}><label>Username<select value={newLoginName} onChange={(event) => setNewLoginName(event.target.value)}><option value="">Choose login</option>{overview?.loginOptions.map((name) => <option key={name} value={name}>{name}</option>)}</select></label><button className="admin-add" disabled={busy || !newLoginName || overview?.logins.some((row) => row.username === newLoginName)} type="submit">Allow login</button></form></div><div className="admin-table-wrap"><table><thead><tr><SortHeader label="Username" column="username" sort={loginSort} onChange={setLoginSort} /><SortHeader label="Code device" column="device" sort={loginSort} onChange={setLoginSort} /><SortHeader label="Allowed" column="approved_at" sort={loginSort} onChange={setLoginSort} /><th>Actions</th></tr></thead><tbody>{loginRows.map((row) => <tr key={row.username}><td className="admin-name">{row.username}</td><td><code>{row.device}</code></td><td>{when(row.approved_at)}</td><td><button className="admin-revoke" onClick={() => void act(async () => {
			const response = await fetch("/api/admin/login", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username: row.username }) });
			if (!response.ok) throw new Error(await responseMessage(response));
			await load();
		})} disabled={row.username === currentUsername} title={row.username === currentUsername ? "You cannot revoke your current login" : `Revoke ${row.username}`}>{row.username === currentUsername ? "Current login" : "Revoke"}</button></td></tr>)}</tbody></table></div></AdminSection>
		<AdminSection title="Approved devices"><div className="admin-section__head"><div className="admin-filters"><label>Owner or device<input placeholder="All devices" value={approvedDeviceFilter} onChange={(event) => setApprovedDeviceFilter(event.target.value)} /></label></div></div><div className="admin-table-wrap"><table><thead><tr><SortHeader label="Device" column="device" sort={approvedSort} onChange={setApprovedSort} /><SortHeader label="Owner" column="owner" sort={approvedSort} onChange={setApprovedSort} /><SortHeader label="Approved" column="approved_at" sort={approvedSort} onChange={setApprovedSort} /><SortHeader label="Last seen" column="last_seen_at" sort={approvedSort} onChange={setApprovedSort} /><th>Actions</th></tr></thead><tbody>{approvedRows.map((row) => <tr key={row.deviceId}><td><code>{row.device}</code></td><td className="admin-name">{row.owner ?? "\u2014"}</td><td>{when(row.approved_at)}</td><td>{when(row.last_seen_at)}</td><td><button className="admin-revoke" onClick={() => void act(async () => {
			const response = await fetch("/api/admin/device", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId: row.deviceId }) });
			if (!response.ok) throw new Error(await responseMessage(response));
			const result = await response.json() as { revokedCurrentDevice: boolean };
			if (result.revokedCurrentDevice) { setAuthenticated(false); setOverview(null); setStatus("This device was revoked."); } else { await load(); }
		})}>Revoke</button></td></tr>)}</tbody></table></div></AdminSection>
		<AdminSection title="Alert subscriptions"><div className="admin-section__head"><div className="admin-filters"><label>Device<input placeholder="All devices" value={subscriptionDeviceFilter} onChange={(event) => setSubscriptionDeviceFilter(event.target.value)} /></label></div></div><div className="admin-table-wrap"><table><thead><tr><SortHeader label="Device" column="device" sort={subscriptionSort} onChange={setSubscriptionSort} /><SortHeader label="Owner" column="owner" sort={subscriptionSort} onChange={setSubscriptionSort} /><SortHeader label="Updated" column="updated_at" sort={subscriptionSort} onChange={setSubscriptionSort} /><SortHeader label="Travel started" column="travel_started_at" sort={subscriptionSort} onChange={setSubscriptionSort} /><SortHeader label="Configuration" column="config" sort={subscriptionSort} onChange={setSubscriptionSort} /></tr></thead><tbody>{subscriptionRows.map((row) => <tr key={row.device}><td><code>{row.device}</code></td><td className="admin-name">{row.owner ?? "\u2014"}</td><td>{when(row.updated_at)}</td><td>{when(row.travel_started_at)}</td><td><pre>{readableJson(row.config)}</pre></td></tr>)}</tbody></table></div></AdminSection>
		<AdminSection title="Journey timeline"><div className="admin-section__head"><div className="admin-filters"><label>Device<select value={deviceFilter} onChange={(event) => setDeviceFilter(event.target.value)}><option value="all">All devices</option>{deviceOptions.map((device) => <option key={device}>{device}</option>)}</select></label><label>Event<input placeholder="All events" value={eventFilter} onChange={(event) => setEventFilter(event.target.value)} /></label></div></div>
			<div className="admin-table-wrap"><table><thead><tr><SortHeader label="Device" column="device" sort={eventSort} onChange={setEventSort} /><SortHeader label="Owner" column="owner" sort={eventSort} onChange={setEventSort} /><SortHeader label="Time" column="at" sort={eventSort} onChange={setEventSort} /><SortHeader label="Event" column="event" sort={eventSort} onChange={setEventSort} /><SortHeader label="Detail" column="detail" sort={eventSort} onChange={setEventSort} /></tr></thead><tbody>{eventRows.map((row) => <tr key={row.id}><td><code>{row.device}</code></td><td className="admin-name">{row.owner ?? "\u2014"}</td><td className="admin-time">{when(row.at)}</td><td>{row.event}</td><td><pre>{readableJson(row.detail)}</pre></td></tr>)}</tbody></table></div>
		</AdminSection>
		{/* Below the operational tables on purpose: these generate things, they do not report on them. */}
		<AdminSection title="Workshop">
		<SpeechPlayground />
		<DigestPlayground />
		<SpeechCache />
		</AdminSection>
	</main>;
}